import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import {
  formatErrorPayload,
  UseApiQueryOptions,
  useCurrentWorkspaceQueryKey,
  validateApiResponse,
} from '../utils';
import { activityRunService } from '~/services/runAPI';

const ROOT_KEY = 'activity_run';
export const getStepRunsQueryKey = (processRunId: string) => [ROOT_KEY, processRunId];
export const getStepRunQueryKey = (processRunId: string, activityRunId: string) => [
  ROOT_KEY,
  processRunId,
  activityRunId,
];
export const getStepRunInfoTypeQueryKey = (type: 'console' | 'events', stepRunId: string) => [
  ROOT_KEY,
  type,
  stepRunId,
];
const getStepRunSocketQueryKey = (processRunId: string, activityRunId: string) => [
  ROOT_KEY,
  'socket',
  processRunId,
  activityRunId,
];

export enum ActivityRunQueryErrors {
  GET_ACTIVITY_RUN_ERROR_ERROR = 'GET_ACTIVITY_RUN_ERROR_ERROR',
}

export const useActivityRunsQuery = (
  { processId, processRunId }: { processId: string; processRunId: string },
  options?: UseApiQueryOptions<typeof activityRunService.getActivityRunsByProcessRun>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    getStepRunsQueryKey(processRunId),
    async () =>
      validateApiResponse(
        await activityRunService.getActivityRunsByProcessRun({
          workspaceId,
          processId,
          processRunId,
        }),
      ).data,
    {
      onError: (error) => {
        dispatch(formatErrorPayload(error));
      },
      ...options,
    },
  );
};

export const useActivityRunQuery = (
  {
    processId,
    processRunId,
    activityRunId,
  }: { processId: string; processRunId: string; activityRunId: string },
  options?: UseApiQueryOptions<typeof activityRunService.getActivityRun>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    getStepRunQueryKey(processRunId, activityRunId),
    async () =>
      validateApiResponse(
        await activityRunService.getActivityRun({
          workspaceId,
          processId,
          processRunId,
          activityRunId,
        }),
      ).data,
    {
      onError: (error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: ActivityRunQueryErrors.GET_ACTIVITY_RUN_ERROR_ERROR }
          ),
        );
      },
      ...options,
    },
  );
};

export const useActivityRunConsoleMessagesQuery = (
  {
    processId,
    processRunId,
    activityRunId,
  }: { processId: string; processRunId: string; activityRunId: string },
  options?: UseApiQueryOptions<typeof activityRunService.getActivityRunConsoleMessages>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    getStepRunInfoTypeQueryKey('console', activityRunId),
    async () =>
      validateApiResponse(
        await activityRunService.getActivityRunConsoleMessages({
          workspaceId,
          processId,
          processRunId,
          activityRunId,
        }),
      ).data,
    {
      onError: (error) => {
        dispatch(formatErrorPayload(error));
      },
      ...options,
    },
  );
};

export const useActivityRunEventsQuery = (
  {
    processId,
    processRunId,
    activityRunId,
  }: { processId: string; processRunId: string; activityRunId: string },
  options?: UseApiQueryOptions<typeof activityRunService.getActivityRunEvents>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    getStepRunInfoTypeQueryKey('events', activityRunId),
    async () =>
      validateApiResponse(
        await activityRunService.getActivityRunEvents({
          workspaceId,
          processId,
          processRunId,
          activityRunId,
        }),
      ).data,
    {
      onError: (error) => {
        dispatch(formatErrorPayload(error));
      },
      ...options,
    },
  );
};

export const useActivityRunSocketInfoQuery = (
  {
    processId,
    processRunId,
    stepRunId,
  }: { processId: string; processRunId: string; stepRunId: string },
  options?: UseApiQueryOptions<typeof activityRunService.getActivityRunSocketInfo>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    getStepRunSocketQueryKey(processRunId, stepRunId),
    async () =>
      validateApiResponse(
        await activityRunService.getActivityRunSocketInfo({
          workspaceId,
          processId,
          processRunId,
          activityRunId: stepRunId,
        }),
      ).data,
    {
      onError: (error) => {
        dispatch(formatErrorPayload(error));
      },
      ...options,
    },
  );
};
